import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URLS } from "../config";
import { getPostLoginPath } from "../utils/postLoginRedirect";
import { FaHubspot } from "react-icons/fa";

const RESEND_COOLDOWN_SEC = 30;

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Only reachable by being redirected from Login.jsx with this state.
  const { pendingToken, maskedEmail } = location.state || {};

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!pendingToken) navigate("/", { replace: true });
  }, [pendingToken, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_URLS.VERIFY_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pendingToken, otp }),
      });
      const data = await response.json();

      if (response.ok) {
        await login(data.accessToken, data.refreshToken, data.user);
        navigate(getPostLoginPath());
      } else {
        setError(data.message || "Invalid or expired code.");
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    setInfo("");

    try {
      const response = await fetch(API_URLS.RESEND_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pendingToken }),
      });
      const data = await response.json();

      if (response.ok) {
        setInfo(data.message || "A new code has been sent.");
        setCooldown(RESEND_COOLDOWN_SEC);
      } else {
        setError(data.message || "Could not resend code.");
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Network error. Please check your connection.");
    } finally {
      setResending(false);
    }
  };

  if (!pendingToken) return null;

  return (
    <section className="Login">
      <div
        className="login-left-panel"
        style={{
          background: `linear-gradient(135deg, rgba(10,10,18,0.75) 0%, rgba(20,18,18,0.5) 100%), url('/login-bg.jpg') center/cover no-repeat`,
        }}
      >
        <div className="login-left-content">
          <h1>Inspection Operations &amp;<br /> Execution Network</h1>
          <p>Streamline your inspection operations with precision and efficiency.</p>
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-card">
          <div className="login-brand">
            <FaHubspot className="login-brand-icon" />
            <span className="login-brand-name">IOEN</span>
          </div>

          <div className="login-heading">
            <h2>Verify your login</h2>
            <p>
              We sent a 6-digit code to{" "}
              {maskedEmail ? <strong>{maskedEmail}</strong> : "your email"}.
            </p>
          </div>

          <form className="login-form" onSubmit={handleVerify}>
            <div className="login-field">
              <label>Verification code</label>
              <div className="login-input-wrapper">
                <input
                  ref={inputRef}
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                    if (error) setError("");
                  }}
                  required
                  disabled={loading}
                  style={{ letterSpacing: "6px", fontSize: "1.1rem", textAlign: "center" }}
                />
              </div>
            </div>

            <div className="login-options">
              <button
                type="button"
                className="login-forgot"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
              >
                {cooldown > 0 ? `Resend code (${cooldown}s)` : resending ? "Resending…" : "Resend code"}
              </button>
            </div>

            {info && <div className="login-forgot-msg">{info}</div>}
            {error && <div className="login-error">{error}</div>}

            <button
              type="submit"
              className="Login-btn login-submit-btn"
              disabled={loading || otp.length !== 6}
            >
              {loading ? "Verifying…" : "Verify & Sign In"}
            </button>
          </form>

          <p className="login-support">
            Wrong account?{" "}
            <button
              type="button"
              className="login-support-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              onClick={() => navigate("/")}
            >
              Back to login
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default VerifyOtp;
